import { useMemo } from 'react';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import type { ComponentTwinsGroupResponseDto } from '@evimo/types';
import type { AssetDetailComponent } from '~/app/services/asset/asset.service';
import { TableRowActionsMenu } from '~/app/components/common/TableRowActionsMenu/TableRowActionsMenu';
import { memberLabels } from '../types';

type Params = {
  componentsById: Map<string, AssetDetailComponent>;
  canUpdate: boolean;
  canDelete: boolean;
  onEdit: (group: ComponentTwinsGroupResponseDto) => void;
  onDelete: (group: ComponentTwinsGroupResponseDto) => void;
};

export function useComponentTwinsGroupsColumns({
  componentsById,
  canUpdate,
  canDelete,
  onEdit,
  onDelete,
}: Params): ColumnsType<ComponentTwinsGroupResponseDto> {
  const { t } = useTranslation('assets');

  return useMemo(() => {
    const columns: ColumnsType<ComponentTwinsGroupResponseDto> = [
      {
        title: t('detail.twins.table.name', 'Group name'),
        dataIndex: 'groupName',
        key: 'groupName',
      },
      {
        title: t('detail.twins.table.members', 'Members'),
        key: 'members',
        render: (_: unknown, record) => memberLabels(record, componentsById),
      },
    ];

    if (canUpdate || canDelete) {
      columns.push({
        title: t('detail.twins.table.actions', 'Actions'),
        key: 'actions',
        width: 56,
        align: 'center',
        render: (_: unknown, record) => (
          <TableRowActionsMenu
            items={[
              ...(canUpdate
                ? [
                    {
                      key: 'edit',
                      label: t('detail.twins.actions.edit', 'Edit'),
                      icon: <EditOutlined />,
                      onClick: () => onEdit(record),
                    },
                  ]
                : []),
              ...(canDelete
                ? [
                    {
                      key: 'delete',
                      label: t('detail.twins.actions.delete', 'Delete'),
                      icon: <DeleteOutlined />,
                      danger: true,
                      onClick: () => onDelete(record),
                    },
                  ]
                : []),
            ]}
          />
        ),
      });
    }

    return columns;
  }, [t, componentsById, canUpdate, canDelete, onEdit, onDelete]);
}
